import TestConnectionButton from "@/bizminer/Admin/Organizations/_components/sso/TestConnectionButton.tsx";
import type { TestStatus } from "@/bizminer/Admin/Organizations/_components/sso/useSsoTestConnection.tsx";
import { Badge } from "@/components/ui/badge";
import type { BizminerAdminSsoConfig } from "@/types/serializers";

const PROVIDERS = [
  { value: "google", label: "Google" },
  { value: "entra_id", label: "Microsoft Entra ID" },
] as const;

const providerLabel = (provider: string | null): string => {
  return PROVIDERS.find((p) => p.value === provider)?.label ?? "Not configured";
};

interface DetailRowProps {
  label: string;
  value: string | null | undefined;
  mono?: boolean;
}

const DetailRow = ({ label, value, mono }: DetailRowProps) => (
  <div>
    <dt className="text-sm font-medium text-muted-foreground">{label}</dt>
    <dd
      className={`mt-1 text-sm text-foreground ${mono ? "font-mono bg-muted px-2 py-1 rounded" : ""}`}
    >
      {value || "—"}
    </dd>
  </div>
);

interface DomainsDisplayProps {
  domains: string[] | null;
}

const DomainsDisplay = ({ domains }: DomainsDisplayProps) => (
  <div className="md:col-span-2">
    <dt className="text-sm font-medium text-muted-foreground">
      Allowed Domains
    </dt>
    <dd className="mt-1 text-sm text-foreground">
      {domains?.length ? (
        <div className="flex flex-wrap gap-1">
          {domains.map((domain) => (
            <Badge key={domain} variant="outline" className="font-mono">
              {domain}
            </Badge>
          ))}
        </div>
      ) : (
        <span className="text-muted-foreground">All domains allowed</span>
      )}
    </dd>
  </div>
);

interface Props {
  ssoConfig: BizminerAdminSsoConfig;
  testStatus: TestStatus;
  onTestConnection: () => void;
}

export default function SsoConfigDisplay({
  ssoConfig,
  testStatus,
  onTestConnection,
}: Props) {
  if (!ssoConfig.sso_enabled) {
    return (
      <div className="text-center py-4">
        <Badge variant="secondary">Disabled</Badge>
        <p className="text-sm text-muted-foreground mt-2">
          SSO is not configured for this organization
        </p>
      </div>
    );
  }

  return (
    <>
      <dl className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <dt className="text-sm font-medium text-muted-foreground">Status</dt>
          <dd className="mt-1">
            <Badge variant="default" className="bg-chart-6">
              Enabled
            </Badge>
          </dd>
        </div>
        <DetailRow
          label="Provider"
          value={providerLabel(ssoConfig.sso_provider)}
        />
        <DetailRow label="Client ID" value={ssoConfig.sso_client_id} mono />
        <DetailRow
          label="Client Secret"
          value={ssoConfig.sso_client_secret_masked}
          mono
        />
        {ssoConfig.sso_provider === "entra_id" && (
          <DetailRow label="Tenant ID" value={ssoConfig.sso_tenant_id} mono />
        )}
        <DomainsDisplay domains={ssoConfig.sso_allowed_domains} />
      </dl>

      {ssoConfig.sso_provider && (
        <div className="mt-4 pt-4 border-t">
          <TestConnectionButton
            testStatus={testStatus}
            onTest={onTestConnection}
          />
        </div>
      )}
    </>
  );
}
